import { useCallback, useEffect, useRef, useState } from "react";
import { Btn, Card, Icon, Input, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { messagingTabs, useMessagingCounts } from "./messaging-tabs";
import { useT } from "../../lib/i18n";
import { LIVE_SOURCE, checkSenderField, type MessagesSource, type SenderField, type SenderView } from "./automatic-message-source";

const sectionTitle: React.CSSProperties = { fontSize: 13.5, fontWeight: 700 };
const label: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase",
  letterSpacing: "0.05em", marginBottom: 6,
};

export function SenderCard({
  state, canRead, canWrite, values, errors, saving, onEdit, onBlur, onRetry, t,
}: {
  state: "loading" | "ready" | "error" | "denied";
  canRead: boolean;
  canWrite: boolean;
  values: { fromName: string; replyTo: string; fromAddress: string | null; fromAddressSource: string | null };
  errors: Partial<Record<SenderField, string>>;
  saving: Partial<Record<SenderField, boolean>>;
  onEdit: (field: SenderField, raw: string) => void;
  onBlur: (field: SenderField) => void;
  onRetry: () => void;
  t: (k: string, f: string) => string;
}) {
  const field = (name: SenderField, labelText: string, hint: string, placeholder: string) => (
    <div>
      <div style={label}>{labelText}</div>
      <Input
        value={values[name]}
        disabled={!canWrite}
        placeholder={placeholder}
        aria-label={labelText}
        aria-invalid={errors[name] ? true : undefined}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEdit(name, e.target.value)}
        onBlur={() => onBlur(name)}
        data-testid={`automail-sender-${name}`}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 5, alignItems: "baseline" }}>
        <span style={{ fontSize: 11.5, color: errors[name] ? T.red1 : T.soft, lineHeight: 1.45, flex: 1 }}>
          {errors[name] ?? hint}
        </span>
        {saving[name] ? (
          <span style={{ fontSize: 11, color: T.soft }}>{t("messaging.auto.saving", "Saving…")}</span>
        ) : null}
      </div>
    </div>
  );

  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "14px 18px 12px", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${T.n6}` }}>
        <span style={{ display: "inline-flex", color: T.p1 }}><Icon.mail size={15} /></span>
        <span style={sectionTitle}>{t("messaging.auto.sender.title", "Sender")}</span>
      </div>
      <div style={{ padding: "16px 18px 18px" }}>
        {!canRead ? (
          <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>
            {t("messaging.auto.sender.denied", "You do not have permission to view sender settings (settings.read).")}
          </div>
        ) : state === "loading" ? (
          <div style={{ fontSize: 12.5, color: T.soft }}>{t("messaging.auto.sender.loading", "Loading sender settings…")}</div>
        ) : state === "error" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: T.red1 }}>
            <span style={{ flex: 1 }}>{t("messaging.auto.sender.loadFailed", "Couldn't load sender settings.")}</span>
            <Btn size="sm" variant="secondary" onClick={onRetry}>{t("messaging.auto.retry", "Retry")}</Btn>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 16, maxWidth: 460 }}>
            {field(
              "fromName",
              t("messaging.auto.sender.fromName", "Sender name"),
              t("messaging.auto.sender.fromNameHint", "Shared with your Brand Kit. Clear it to use the default."),
              t("messaging.auto.sender.fromNamePlaceholder", "Your organization"),
            )}
            <div>
              <div style={label}>{t("messaging.auto.sender.fromAddress", "Sending address")}</div>
              <div
                style={{
                  fontSize: 13, color: values.fromAddress ? T.n1 : T.soft,
                  fontFamily: values.fromAddress ? T.fontMono : T.font,
                  padding: "9px 12px", background: T.cardSoft, border: `1px solid ${T.n6}`,
                  borderRadius: 8, wordBreak: "break-all",
                }}
              >
                {values.fromAddress ?? t("messaging.auto.sender.notAvailable", "Not available")}
              </div>
              <div style={{ fontSize: 11.5, color: T.soft, marginTop: 5, lineHeight: 1.45 }}>
                {values.fromAddress
                  ? t("messaging.auto.sender.fromAddressReadOnly", "Set in your verified sending domain — read-only here.")
                  : t("messaging.auto.sender.fromAddressUnresolved", "No sending address could be resolved. Check your verified sending domain.")}
              </div>
            </div>
            {field(
              "replyTo",
              t("messaging.auto.sender.replyTo", "Reply-to address"),
              t("messaging.auto.sender.replyToHint", "Clear it to send no Reply-To header."),
              t("messaging.auto.sender.replyToPlaceholder", "replies@example.com"),
            )}
            {!canWrite ? (
              <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>
                {t("messaging.auto.sender.readOnly", "You do not have permission to change sender settings (settings.write).")}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}


/** Sender settings, separated from the retired automatic-message catalogue. */
export default function MessagingSettings({ source = LIVE_SOURCE }: { source?: MessagesSource }) {
  const t = useT();
  const nav = useNav();
  const counts = useMessagingCounts();
  const canSettingsRead = can("settings.read");
  const canSettingsWrite = can("settings.write");
  const [senderState, setSenderState] = useState<"loading" | "ready" | "error" | "denied">(
    canSettingsRead ? "loading" : "denied",
  );
  const [sender, setSender] = useState<SenderView>({ fromName: null, replyTo: null, fromAddress: null, fromAddressSource: null });
  const [draft, setDraft] = useState<{ fromName: string; replyTo: string }>({ fromName: "", replyTo: "" });
  const [senderErrors, setSenderErrors] = useState<Partial<Record<SenderField, string>>>({});
  const [senderSaving, setSenderSaving] = useState<Partial<Record<SenderField, boolean>>>({});
  /** Card-level sequence: one queue across BOTH fields (§Sender card). */
  const senderSeq = useRef(0);
  /** Fields with an edit newer than the response being reconciled. */
  const dirty = useRef<Set<SenderField>>(new Set());
  const confirmed = useRef<{ fromName: string; replyTo: string }>({ fromName: "", replyTo: "" });

  const loadSender = useCallback(() => {
    if (!canSettingsRead) { setSenderState("denied"); return; }
    setSenderState("loading");
    source.loadSender()
      .then((s) => {
        setSender(s);
        const next = { fromName: s.fromName ?? "", replyTo: s.replyTo ?? "" };
        setDraft(next);
        confirmed.current = next;
        setSenderState("ready");
      })
      .catch(() => setSenderState("error"));
  }, [canSettingsRead, source]);

  useEffect(() => { loadSender(); }, [loadSender]);

  const editSender = (field: SenderField, raw: string) => {
    dirty.current.add(field);
    setDraft((d) => ({ ...d, [field]: raw }));
    if (senderErrors[field]) setSenderErrors((e) => ({ ...e, [field]: undefined }));
  };

  const blurSender = (field: SenderField) => {
    const raw = draft[field];
    if (raw === confirmed.current[field]) { dirty.current.delete(field); return; }
    const checked = checkSenderField(field, raw);
    if (!checked.ok) {
      // Preserve the entered text; bind the error to its field; do not send.
      setSenderErrors((e) => ({ ...e, [field]: checked.message }));
      return;
    }
    const seq = ++senderSeq.current;
    setSenderSaving((s) => ({ ...s, [field]: true }));
    source.saveSender({ [field]: checked.value } as { fromName?: string | null; replyTo?: string | null })
      .then((server) => {
        setSender(server);
        dirty.current.delete(field);
        // Out-of-order guard: apply a response field only when nothing newer is
        // pending for it, so an older response cannot clobber a newer edit to
        // the OTHER field either.
        if (seq === senderSeq.current) {
          setDraft((d) => ({
            fromName: dirty.current.has("fromName") ? d.fromName : server.fromName ?? "",
            replyTo: dirty.current.has("replyTo") ? d.replyTo : server.replyTo ?? "",
          }));
        }
        confirmed.current = {
          ...confirmed.current,
          [field]: field === "fromName" ? server.fromName ?? "" : server.replyTo ?? "",
        };
        setSenderErrors((e) => ({ ...e, [field]: undefined }));
      })
      .catch((e: unknown) => {
        // Keep the unconfirmed input available for correction; show the error.
        setSenderErrors((errs) => ({
          ...errs,
          [field]: e instanceof Error ? e.message : t("messaging.auto.sender.saveFailed", "Couldn't save. Try again."),
        }));
      })
      .finally(() => setSenderSaving((s) => ({ ...s, [field]: false })));
  };

  return (
    <Screen rail="messaging" tabs={messagingTabs(nav, "settings", counts)} activeTab="settings">
      <PageHeader
        breadcrumb={null}
        title={t("messaging.settings.title", "Message settings")}
        subtitle={t("messaging.settings.subtitle", "Who these emails come from, and where customer replies go.")}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 980 }}>
        <SenderCard
          state={senderState}
          canRead={canSettingsRead}
          canWrite={canSettingsWrite}
          values={{
            fromName: draft.fromName,
            replyTo: draft.replyTo,
            fromAddress: sender.fromAddress,
            fromAddressSource: sender.fromAddressSource,
          }}
          errors={senderErrors}
          saving={senderSaving}
          onEdit={editSender}
          onBlur={blurSender}
          onRetry={loadSender}
          t={t}
        />
      </div>
    </Screen>
  );
}
